import { Box, Modal, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  useGetEngineeringTicketPlatformIntegrationConfigsQuery,
  useGetEngineeringTicketPlatformIntegrationsQuery,
  useGetGithubIntegrationsQuery,
  useGetMessagingPlatformIntegrationsQuery,
} from "../../__generated__/graphql";
import { useCompany } from "../../contexts/CompanyContext";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import GitHubLogo from "../../images/githubLogo.png";
import ZendeskLogo from "../../images/zendeskLogo.png";
import { INTEGRATIONS } from "../../utils/constants";
import { SettingsComponentProps } from "../../utils/interfaces";
import ActionBanner from "../ActionBanner";
import IntegrationButton from "../IntegrationButton";
import LoadingAnimation from "../LoadingAnimation";

import SlackIntegrationSection from "./Integrations/SlackIntegrationSection";
import JiraIntegrationSection from "./JiraIntegrationSection";
import LinearIntegrationSection from "./LinearIntegrationSection";
import Section from "./Section";

const {
  GITHUB,
  GITHUB_APP_INSTALLATION_URL,
  JIRA,
  LINEAR,
  SLACK,
  ZENDESK,
  getZendeskOauthUri,
} = INTEGRATIONS;

const TAB_NAME = "integrations";
const DISCARD_EVENT_NAME = `discard:${TAB_NAME}`;

const Integrations: React.FC<SettingsComponentProps> = ({ section }) => {
  const [zendeskSubdomain, setZendeskSubdomain] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedCompany } = useCompany(); // Use the useCompany hook to get the selected company
  const companyId = selectedCompany
    ? parseInt(selectedCompany.value, 10)
    : undefined;
  const { data, loading, error, refetch } =
    useGetEngineeringTicketPlatformIntegrationsQuery({
      variables: { companyId },
    });

  const {
    data: dataGithubIntegrations,
    loading: loadingGithubIntegrations,
    error: errorGithubIntegrations,
  } = useGetGithubIntegrationsQuery({
    variables: { companyId },
  });

  const {
    data: dataMessagingIntegrations,
    loading: loadingMessagingIntegrations,
    error: errorMessagingIntegrations,
  } = useGetMessagingPlatformIntegrationsQuery({
    variables: { companyId },
  });

  const jiraData = data?.getEngineeringTicketPlatformIntegrations?.find(
    (item) => item?.provider === JIRA,
  );
  const linearData = data?.getEngineeringTicketPlatformIntegrations?.find(
    (item) => item?.provider === LINEAR,
  );
  const zendeskData = data?.getEngineeringTicketPlatformIntegrations?.find(
    (item) => item?.provider === ZENDESK,
  );
  const slackData =
    dataMessagingIntegrations?.getMessagingPlatformIntegrations?.find(
      ({ provider }) =>
        provider.toLocaleLowerCase() === SLACK.toLocaleLowerCase(),
    );

  const { data: integrationConfigsData } =
    useGetEngineeringTicketPlatformIntegrationConfigsQuery({
      variables: { companyId },
    });

  const zendeskConfigs =
    integrationConfigsData?.getEngineeringTicketPlatformIntegrationConfigs?.find(
      (item) => item?.provider === ZENDESK,
    );
  const jiraClientId =
    integrationConfigsData?.getEngineeringTicketPlatformIntegrationConfigs?.find(
      (item) => item?.provider === JIRA,
    )?.clientId || "";
  const linerClientId =
    integrationConfigsData?.getEngineeringTicketPlatformIntegrationConfigs?.find(
      (item) => item?.provider === LINEAR,
    )?.clientId || "";

  const { isDirty, clearUnsavedChanges, externalSave, getExternalFunction } =
    useUnsavedChanges();

  const zendeskInstalled = zendeskData?.installed;
  const zendeskClientId = zendeskConfigs?.clientId;
  const githubInstalled =
    dataGithubIntegrations?.getGithubIntegrations.length! > 0;

  const handleSaveChanges = useCallback(async () => {
    if (isDirty(TAB_NAME)) {
      const fn = externalSave(TAB_NAME);
      if (fn) return await fn;
    }
  }, [externalSave, isDirty]);

  const handleDiscardChanges = useCallback(async () => {
    const fn = getExternalFunction(DISCARD_EVENT_NAME);
    await fn?.();
    clearUnsavedChanges(TAB_NAME);
  }, [getExternalFunction, clearUnsavedChanges]);

  // Refetch the query whenever the selectedCompany changes
  useEffect(() => {
    refetch({ companyId });
  }, [companyId, refetch]);

  const storeZendeskSubdomainInLocalStorage = () =>
    localStorage.setItem("zendeskSubdomain", zendeskSubdomain);

  return (
    <>
      {loading ? (
        <LoadingAnimation text='Loading...' />
      ) : error ? (
        <p>Error: {error.message}</p>
      ) : (
        <>
          <LinearIntegrationSection
            section={section}
            tabName={TAB_NAME}
            discardName={DISCARD_EVENT_NAME}
            companyId={companyId}
            clientId={linerClientId}
            data={linearData}
          />
          <Section title={GITHUB} hide={section !== GITHUB.toLowerCase()}>
            {/* TODO - remove below once we have LLM service(s) in place */}
            <div className='text-3xl text-blue-500 font-bold mb-4'>
              Beta release
            </div>
            {githubInstalled ? (
              <div className='space-y-4'>
                {dataGithubIntegrations?.getGithubIntegrations.map(
                  (integration) => (
                    <IntegrationButton
                      key={integration.installationId}
                      logo={GitHubLogo}
                      // Display the organization name along with GitHub label.
                      name={`${integration.installedOnAccount}`}
                      nameTextPreamble='Edit installation for organization:'
                      // Link to a page for managing this particular integration.
                      to={`https://github.com/organizations/${integration.installedOnAccount}/settings/installations/${integration.installationId}`}
                    />
                  ),
                )}
                <br />
                {/* Optionally, a button to add more installations */}
                <IntegrationButton
                  logo={GitHubLogo}
                  nameTextPreamble=''
                  name='Add more GitHub organizations'
                  to={GITHUB_APP_INSTALLATION_URL}
                />
              </div>
            ) : (
              <div className='flex items-center'>
                <IntegrationButton
                  logo={GitHubLogo}
                  name={GITHUB}
                  to={GITHUB_APP_INSTALLATION_URL}
                />
              </div>
            )}
          </Section>
          {/* CURRENTLY IN-PROGRESS...start */}
          <SlackIntegrationSection
            section={section}
            tabName={TAB_NAME}
            discardName={DISCARD_EVENT_NAME}
            companyId={companyId}
            data={slackData}
          />
          {/* CURRENTLY IN-PROGRESS...end */}
          <Section title={ZENDESK} hide={section !== ZENDESK.toLowerCase()}>
            {zendeskInstalled ? (
              <div className='space-y-4'>Zendesk Integrated!!!</div>
            ) : (
              <div className='flex items-center'>
                <IntegrationButton
                  logo={ZendeskLogo}
                  name={ZENDESK}
                  to={window.location.href}
                  onClick={() => setIsModalOpen(true)}
                />
                <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                  <Box
                    sx={{
                      p: 4,
                      backgroundColor: "white",
                      margin: "auto",
                      borderRadius: "8px",
                      width: "400px",
                      marginTop: "320px",
                    }}
                  >
                    <h2
                      className='text-primary-blue-100 font-bold mb-2'
                      data-testid='zendeskModalTitle'
                    >
                      Please specify your Zendesk subdomain
                    </h2>
                    <TextField
                      label='Subdomain'
                      fullWidth
                      value={zendeskSubdomain}
                      onChange={(e) => setZendeskSubdomain(e.target.value)}
                      required
                      sx={{ marginBottom: "10px" }}
                      data-testid='subdomainInput'
                    />
                    {/* Zendesk integration */}
                    <NavLink
                      to={`${getZendeskOauthUri(
                        zendeskSubdomain,
                      )}&client_id=${zendeskClientId}`}
                      className='link'
                      target='_blank'
                    >
                      <button
                        className='flex items-center bg-sky-100 text-primary-blue-100 px-4 py-2 shadow rounded-md gap-1 text-sm font-medium'
                        onClick={() => {
                          storeZendeskSubdomainInLocalStorage();
                          setIsModalOpen(false);
                        }}
                        data-testid='connectIntegrationButton'
                      >
                        Connect integration
                      </button>
                    </NavLink>
                  </Box>
                </Modal>
              </div>
            )}
          </Section>
          <JiraIntegrationSection
            section={section}
            tabName={TAB_NAME}
            discardName={DISCARD_EVENT_NAME}
            companyId={companyId}
            clientId={jiraClientId}
            data={jiraData}
          />

          {isDirty(TAB_NAME) && (
            <ActionBanner
              onSave={() => handleSaveChanges()}
              onDiscard={() => handleDiscardChanges()}
            />
          )}
        </>
      )}
    </>
  );
};

export default Integrations;
